import React, { useContext, useState } from 'react'
import { Link, RouteComponentProps, useHistory } from 'react-router-dom'
import useFarmingPools from '../../hooks/useFarmingPools'
import LoaderCustom from '../../components/LoaderCustom'
import { useMasterChief, usePairContract } from '../../hooks/useContract'
import styled, { ThemeContext } from 'styled-components'
import { useActiveWeb3React } from '../../hooks'

import { ButtonPrimary } from '../../components/Button'
import { useToken } from '../../hooks/Tokens'
import DoubleCurrencyLogo from '../../components/DoubleLogo'
import Stake from './Stake'
import Unstake from './Unstake'
import { useWalletModalToggle } from '../../state/application/hooks'
import { calculateGasMargin, getContract } from '../../utils'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { formatUnits } from 'ethers/lib/utils'
import { BigNumber } from 'ethers'
import { roundToDecimalPlace } from '../../utils/data'
import { SkeletonText } from '../../components/Skeleton'
import { Currency, Token, WETH9 } from '@uniswap/sdk-core'
import { CURRENT_CHAIN, MASTERCHIEF_ADDRESS } from '@hyperswap/addresses'
import { abi as MasterChief_ABI } from '../../abis/masterchief.json'
import IntroCard from 'components/IntroCard'
import { TYPE } from 'theme'
import axios from 'axios'
import { API_POOL_INDEXED_BASE_URL } from 'constants/links'
import { Helmet } from 'react-helmet'

export const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 14px;
  display: flex;
  gap: 2px;
`

export const StyledTabButton = styled.button<{ isActive?: boolean }>`
  ${({ theme }) => theme.flexRowNoWrap}
  border: none;
  align-items: center;
  justify-content: center;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: white;
  padding: 8px 16px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 14px;
  transition: all 300ms;
  background: ${({ isActive }) => (isActive ? '#4AFC38' : 'transparent')};
  color: ${({ isActive }) => (isActive ? 'black' : 'white')};

  &:hover {
    color: #4afc38;
  }

  ${({ isActive }) =>
    isActive &&
    `
      &:hover {
        color: black !important;
      }
    `}
`

const ContainerStyled = styled.div`
  width: 100%;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 200px;
  @media (min-width: 502px) {
    padding: 10px;
  }
`

const LinkStyled = styled(Link)`
  display: flex;
  width: 100%;
  color: white;
  font-size: 18px;
  font-weight: 600;
  gap: 14px;
  text-decoration: none;
  align-items: center;
  padding: 20px 10px;
  background: ${({ theme }) => theme.bg1}
  border-radius: 14px;
  transition: all 300ms;

  svg {
    height: 30px;
    width: 30px;
  }

  &:hover {
     background: ${({ theme }) => theme.bg2};
  }
`

const GridContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: calc(55% - 8px) calc(45% - 8px);
  }
`

const EntriesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0px, 1fr));
  gap: 15px 0.5rem;
  padding: 0;
`

const SubContainer = styled.div<{ bg1: boolean }>`
  border-radius: 14px;
  background: ${({ theme }) => theme.bg0};

  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`
const SubContainerHeader = styled.div`
  border-radius: 14px; 
  background: ${({ theme }) => theme.bg1}
  padding: 20px;
`

const SubContainerBody = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.bg0};
`

const ConnectWalletContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  min-height: 25vh;
  background: ${({ theme }) => theme.bg0};
  padding: 8px;
  border-radius: 14px;
  width: 100%;

  div {
    max-width: 300px;
  }
`

const FarmPool = ({
  match: {
    params: { lptoken },
  },
}: RouteComponentProps<{ lptoken: string }>) => {
  const history = useHistory()
  const { active, account, chainId, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const [tab, setTab] = useState<0 | 1>(0)
  const masterChiefContract = useMasterChief()
  const { pools, loading, refresh: refreshPools } = useFarmingPools()
  const [pool, setPool] = React.useState<any>(null)
  const PairContract = usePairContract(pool?.lpToken)
  const [token0, setToken0] = React.useState('')
  const [token1, setToken1] = React.useState('')
  const [user, setUser] = React.useState<any>()
  const [pId, setPId] = React.useState(-1)
  const [pairToken, setPairToken] = React.useState<undefined | Token>(undefined)
  const [pendingRewards, setPendingRewards] = React.useState<BigNumber | null | undefined>(BigNumber.from(0))
  const [refresh, setRefresh] = React.useState<boolean>(true)
  const theme = useContext(ThemeContext)
  const [apr, setApr] = React.useState<string | null>(null)

  const [loadingClaim, setLoadingClaim] = React.useState<boolean>(false)

  const token0Currency = useToken(token0)
  const token1Currency = useToken(token1)
  const againstWETHCurrency = token1Currency?.address === WETH9[CURRENT_CHAIN].address ? token0Currency : token1Currency

  React.useEffect(() => {
    const poolId = pools.findIndex((pool: any) => pool.lpToken === lptoken)
    if (loading === false && pools.length > 0 && poolId === -1) {
      history.push('/farm')
    } else {
      setPId(poolId)
      setPool(pools[poolId])
    }
  }, [pools, loading, lptoken, history])

  React.useEffect(() => {
    const fetchTokens = async () => {
      if (PairContract) {
        try {
          const token0Address = await PairContract.token0()
          const token1Address = await PairContract.token1()
          setToken0(token0Address)
          setToken1(token1Address)
        } catch (error) {
          console.error('Error fetching token addresses:', error)
        }
      }
    }

    fetchTokens()
  }, [PairContract])

  React.useEffect(() => {
    const fetchAPR = async (
      againstWETHCurrency: Token | null,
      setApr: React.Dispatch<React.SetStateAction<string | null>>
    ) => {
      if (!againstWETHCurrency) return

      try {
        const aprResponse = await axios.get(
          `${API_POOL_INDEXED_BASE_URL}/api/apr?token1=${againstWETHCurrency.address}`
        )
        if (aprResponse.data.success === true) {
          setApr(aprResponse.data.data.apr)
        } else {
          console.error('Failed to fetch APR:', aprResponse.data.message)
        }
      } catch (error) {
        console.error('Error fetching APR:', error)
      }
    }

    const fetchUserInfo = async () => {
      if (
        chainId &&
        pId > -1 &&
        account &&
        active &&
        masterChiefContract &&
        token0Currency &&
        token1Currency &&
        againstWETHCurrency
      ) {
        try {
          fetchAPR(againstWETHCurrency, setApr)

          const userInfo = await masterChiefContract.userInfo(pId, account)
          const pendingRewardsInfo = await masterChiefContract.pendingReward(pId, account)
          const pairToken = new Token(
            chainId,
            pool?.lpToken,
            18,
            `${token0Currency?.symbol}-${token1Currency?.symbol}`,
            `${token0Currency?.symbol}-${token1Currency?.symbol}`
          )
          setPendingRewards(pendingRewardsInfo)
          setPairToken(pairToken)
          setUser(userInfo)
          setRefresh(false)
        } catch (error) {
          setRefresh(false)
          console.error('Error fetching user info:', error)
        }
      }
    }

    if (refresh) {
      fetchUserInfo()
    }
  }, [
    account,
    pId,
    masterChiefContract,
    token0Currency,
    token1Currency,
    chainId,
    active,
    pool,
    refresh,
    againstWETHCurrency,
  ])

  React.useEffect(() => {
    refreshPools()
    setRefresh(true)
    // eslint-disable-next-line
  }, [account])

  const addTransaction = useTransactionAdder()

  async function claim() {
    if (!chainId || !library || !account || !masterChiefContract) return
    const masterChief = getContract(MASTERCHIEF_ADDRESS, MasterChief_ABI, library, account)

    const estimate = masterChief.estimateGas.deposit
    const method = masterChief.deposit
    const args = [pId, 0]

    setLoadingClaim(true)

    await estimate(...args, {})
      .then((estimatedGasLimit) =>
        method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Claim rewards`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          setRefresh(true)
          setLoadingClaim(false)
        })
      )
      .catch((error) => {
        setLoadingClaim(false)
        console.error(error)
      })
  }

  const isDisabled = pendingRewards?.isZero() || loadingClaim === true
  const rewardsFormatted = formatUnits(pendingRewards ? pendingRewards : BigNumber.from(0), 18)

  const staked = user?.amount
  const stakedFormatted = formatUnits(staked ? staked : BigNumber.from(0), 18)
  const totalDeposit = pool?.totalDeposit
  const totalDepositFormatted = formatUnits(totalDeposit ? totalDeposit : BigNumber.from(0), 18)

  let myShareFormatted = '0'

  if (staked && !staked.isZero() && !totalDeposit.isZero()) {
    myShareFormatted = ((parseFloat(stakedFormatted) / parseFloat(totalDepositFormatted)) * 100).toFixed(2).toString()

    if (myShareFormatted === '0.00') myShareFormatted = '<0.01'
  }

  const randomKey = Math.floor(Math.random() * 999999) // In order to re-render Stake and Unstake

  return (
    <>
      <Helmet>
        <title>Farm | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Stake your LP tokens in farm pools on HyperSwap Exchange. Earn high rewards securely on Hyperliquid's EVM blockchain."
        />
        <meta
          name="keywords"
          content="HyperSwap farm pools, staking rewards, Hyperliquid blockchain, DeFi farming, earn crypto, Hyperliquid EVM"
        />
        <meta property="og:title" content="Farm Pools | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Earn rewards by staking in farm pools on HyperSwap Exchange, running on the Hyperliquid blockchain."
        />
        <meta property="og:url" content={`${window.location.origin}/#/farm`} />
      </Helmet>
      <ContainerStyled>
        <IntroCard variant={1} key="Farm pool">
          <h1 style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {token0Currency && token1Currency ? (
              <>
                <DoubleCurrencyLogo
                  currency0={token0Currency as Currency}
                  currency1={token1Currency as Currency}
                  size={42}
                  margin={true}
                />
                {`${token0Currency?.symbol}-${token1Currency?.symbol} Farming Pool`}
              </>
            ) : (
              <>Farming Pool</>
            )}
          </h1>
        </IntroCard>
        <LinkStyled to="/farm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M15 6l-6 6l6 6" />
          </svg>
          Manage Farm
        </LinkStyled>
        {account ? (
          <>
            <GridContainer>
              <SubContainer bg1={false}>
                {loading === false && token0Currency && token1Currency ? (
                  <>
                    <SubContainerHeader>
                      <div style={{ display: 'flex', fontSize: '16px', marginBottom: '12px' }}>
                        <DoubleCurrencyLogo
                          currency0={token0Currency as Currency}
                          currency1={token1Currency as Currency}
                          size={42}
                          margin={true}
                        />
                        <h2
                          style={{ fontSize: '16px', marginLeft: '12px' }}
                        >{`${token0Currency?.symbol}-${token1Currency?.symbol}`}</h2>
                      </div>

                      <EntriesContainer>
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>Liquidity</span>
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>Pool APR</span>
                        <span style={{ fontSize: '14px', fontWeight: 600 }}>Farm APR</span>
                      </EntriesContainer>
                      <EntriesContainer>
                        <span style={{ fontSize: '12px', fontWeight: 400 }}>
                          {roundToDecimalPlace(totalDepositFormatted, '2')} HSPX-LP
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 400 }}>
                          {apr ? `${apr}%` : <SkeletonText width="40%" height="100%" />}
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: 400 }}>
                          <SkeletonText width="55%" height="100%" />
                        </span>
                      </EntriesContainer>
                    </SubContainerHeader>

                    <SubContainerBody>
                      <div style={{ marginTop: '16px' }}>
                        <EntriesContainer>
                          <span style={{ fontWeight: 600, fontSize: '16px' }}>Staked</span>
                          <span style={{ fontWeight: 600, fontSize: '16px' }}>Your Share</span>
                        </EntriesContainer>
                        <EntriesContainer>
                          <span style={{ fontSize: '20px', fontWeight: 600, color: '#4afc38' }}>
                            {roundToDecimalPlace(stakedFormatted, '2')} HSPX-LP
                          </span>
                          <span style={{ fontSize: '20px', fontWeight: 600, color: '#4afc38' }}>
                            {myShareFormatted}%
                          </span>
                        </EntriesContainer>
                      </div>
                      <div style={{ marginTop: '30px' }}>
                        <EntriesContainer>
                          <span style={{ fontWeight: 600, fontSize: '16px' }}>Pending Rewards</span>
                        </EntriesContainer>
                        <EntriesContainer>
                          <span style={{ fontSize: '20px', fontWeight: 600, color: '#4afc38' }}>
                            {roundToDecimalPlace(rewardsFormatted, '6')} $xHSPX
                          </span>
                        </EntriesContainer>
                      </div>
                      <div style={{ marginTop: '20px' }}>
                        <ButtonPrimary disabled={isDisabled} onClick={() => claim()}>
                          {loadingClaim ? 'Claiming...' : 'Claim'}
                        </ButtonPrimary>
                      </div>
                    </SubContainerBody>
                  </>
                ) : (
                  <LoaderCustom />
                )}
              </SubContainer>
              <div>
                <SubContainer bg1={false}>
                  <SubContainerHeader>
                    <Tabs>
                      <StyledTabButton isActive={tab === 0} onClick={() => setTab(0)}>
                        Stake
                      </StyledTabButton>
                      <StyledTabButton isActive={tab === 1} onClick={() => setTab(1)}>
                        Unstake
                      </StyledTabButton>
                    </Tabs>

                    {tab === 0 ? (
                      <p style={{ color: 'lightgrey', marginBottom: '0', fontSize: '14px' }}>
                        Please enter the amount of LP Tokens you wish to stake.
                      </p>
                    ) : (
                      <p style={{ color: 'lightgrey', marginBottom: '0', fontSize: '14px' }}>
                        Please enter the amount of LP Tokens you wish to unstake.
                      </p>
                    )}
                  </SubContainerHeader>

                  {tab === 0 ? (
                    <Stake
                      key={randomKey}
                      setRefresh={() => {
                        refreshPools()
                        setRefresh(true)
                      }}
                      pairToken={pairToken}
                      pId={pId}
                      token0Currency={token0Currency}
                      token1Currency={token1Currency}
                    />
                  ) : (
                    <Unstake
                      key={randomKey + 1}
                      setRefresh={() => {
                        refreshPools()
                        setRefresh(true)
                      }}
                      pId={pId}
                      token0Currency={token0Currency}
                      token1Currency={token1Currency}
                      user={user}
                    />
                  )}
                </SubContainer>
              </div>
            </GridContainer>
          </>
        ) : (
          <ConnectWalletContainer>
            <div>
              <TYPE.mediumHeader textAlign="center" color={theme.text3}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-tractor"
                  style={{ marginBottom: '.5rem' }}
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M7 15m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
                  <path d="M7 15l0 .01" />
                  <path d="M19 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                  <path d="M10.5 17l6.5 0" />
                  <path d="M20 15.2v-4.2a1 1 0 0 0 -1 -1h-6l-2 -5h-6v6.5" />
                  <path d="M18 5h-1a1 1 0 0 0 -1 1v4" />
                </svg>

                <div>{'Connect your wallet to see the farming pool details.'}</div>
              </TYPE.mediumHeader>
              <ButtonPrimary style={{ marginTop: '2em', padding: '8px 16px' }} onClick={toggleWalletModal}>
                Connect a wallet
              </ButtonPrimary>
            </div>
          </ConnectWalletContainer>
        )}
      </ContainerStyled>
    </>
  )
}
export default FarmPool
